import React from "react";
import { GoldDivider } from "@/components/luxury";

export default function HowWeVerifyAKennel() {
  return (
    <>
      <p className="font-spectral italic text-xl md:text-2xl tracking-tight text-[#3a3a3a] leading-snug max-w-2xl">
        A "verified" badge that means nothing devalues every badge on the
        platform. So we made ours hard to earn — and harder to fake.
      </p>

      <GoldDivider variant="ornament" className="max-w-md my-10" />

      <p>
        Every gold WaxSeal you see on a kennel profile, marketplace
        listing, or Buyer&nbsp;Pass tier card means a human at Armstrong
        K9 personally signed off on a seven-step verification record —
        identity, premises, microchip uniqueness, pedigree resolution,
        breeding-history plausibility, Stripe Connect, and two
        independent buyer references. Three of those seven are
        automated against live registry data; the other four require
        an admin to review evidence and mark the step <em>passed</em>.
        The seal can&rsquo;t be conferred until every step is green.
      </p>

      <h2>1. Identity matches the kennel</h2>
      <p>
        Government-issued ID. The name on it has to be the registered
        owner of the kennel — or, if a corporate entity owns the kennel,
        a director. We don't accept "my partner runs it" or "we're family-owned." Buyers need to know exactly which legal person is on the
        other end of a $5,000 puppy transaction.
      </p>

      <h2>2. Premises are real</h2>
      <p>
        We run a public-records check on the address, then we ask for
        recent dated photos: kennel runs, whelping space, exercise yard.
        Not staged. Not a stock photo. Real dogs, real dates. Anything
        from a Google reverse-image search disqualifies on the spot.
      </p>

      <h2>3. Microchip uniqueness across the registry</h2>
      <p>
        Every dog in the kennel has to be microchip-registered, and
        every chip number has to be unique across the entire Armstrong
        K9 database. The check happens at registration time, not at
        verification time, so a kennel that's been quietly registering
        the same dog twice will already have flagged itself before this
        review.
      </p>

      <h2>4. Pedigree ancestors resolve</h2>
      <p>
        A kennel claiming a 4-generation Cane Corso pedigree should be
        able to point us to the registered records of at least the sire
        and dam. If the parents don't exist anywhere we can audit, the
        pedigree is decorative — and we say so.
      </p>

      <h2>5. Breeding history is plausible</h2>
      <p>
        Litter frequency, dam recovery time between litters, sire age at
        first breeding. These aren't moralised standards — every breeder
        sets their own — but a record that shows three back-to-back
        litters from the same dam in eighteen months reads as a
        commercial operation, and we'd rather not pretend otherwise on a
        kennel profile.
      </p>

      <h2>6. Stripe Connect onboarding completed</h2>
      <p>
        For kennels accepting payments through the platform, the Stripe
        Connect identity verification has to be green. This is a
        technical check, but it doubles as a money-laundering safeguard
        — buyers know that the funds they send leave a regulated trail.
      </p>

      <h2>7. References from at least two prior buyers</h2>
      <p>
        We email two recent buyers picked from the kennel's transaction
        history. Both have to respond. We don't tell the kennel which
        two we chose. If the buyers don't reply, or if one comes back
        with a complaint we can't reconcile, the kennel doesn't get the
        seal until the issue is closed.
      </p>

      <GoldDivider variant="ornament" className="max-w-md my-10" />

      <h2>Why we walk away</h2>
      <p>
        We've started the review process with kennels that looked
        excellent on paper and ended it without conferring the seal —
        because something off-platform didn't add up, because a
        reference came back ambivalent, because the legal entity didn't
        match the public record. None of those kennels are exposed; we
        don't publicly delist anyone for failing a verification we
        offered, because failing a private review isn't a public
        offence.
      </p>
      <p>
        But we also don't pretend the seal is ceremonial. The buyers
        sending five-figure deposits have to know that the gold mark
        means something the next time they see it.
      </p>
    </>
  );
}
